interface FormPanelProps {
    el: HTMLElement;
}

const FormPanel = ({ el }: FormPanelProps): void => {
    const wrapper = el;
    const isFullWidth = wrapper.classList.contains('is-full-width');
    const sidePanel: HTMLElement | null = document.querySelector('.side-panel');
    const contentShift: HTMLElement | null = document.querySelector('.content-shift');
    const innerContentPanel: HTMLElement | null = document.querySelector('.inner-content-panel');
    const registerPrompt: HTMLElement | null = document.querySelector('.register-prompt');
    const showMoreCta = document.querySelector('.show-more-cta') as HTMLElement | null;
    const sidePanelMoreDetails = document.querySelector('.side-panel-more-details') as HTMLElement | null;

    // let registerPromptHeight = 0;

    const manageStickyBar = (): void => {
        const isMobileScreenSize = window.outerWidth <= 767;
        if (!registerPrompt || !innerContentPanel) {
            return;
        }

        if (!isMobileScreenSize) {
            registerPrompt.classList.remove('sticky-prompt');
            return;
        }

        const contentBoxRect = innerContentPanel.getBoundingClientRect();

        const stopStickyPointRelativeToViewport = contentBoxRect.bottom;

        const viewportBottom = window.innerHeight;

        if (stopStickyPointRelativeToViewport <= viewportBottom) {
            registerPrompt.classList.remove('sticky-prompt');
        } else {
            registerPrompt.classList.add('sticky-prompt');
        }
    };

    const updateContentBoxPadding = (): void => {
        const isMobileScreenSize = window.outerWidth <= 767;
        if (!innerContentPanel || !registerPrompt) {
            return;
        }

        if (!isMobileScreenSize) {
            innerContentPanel.style.removeProperty('padding-bottom');
        }
    };

    const adjustFormPanelPositioning = (): void => {
        if (contentShift && sidePanel) {
            if (window.innerWidth < 768) {
                sidePanel!.style.marginTop = `${contentShift!.clientHeight}px`;
            } else {
                sidePanel!.style.marginTop = '0px';
            }
        }

        if (contentShift && isFullWidth && innerContentPanel) {
            innerContentPanel.style.marginTop = `${contentShift!.clientHeight}px`;
        }
    };

    // Function to toggle the 'show' class on sidePanelMoreDetails
    const toggleMoreDetails = (): void => {
        if (sidePanelMoreDetails) {
            sidePanelMoreDetails.classList.toggle('show');
            if (sidePanelMoreDetails.classList.contains('show')) {
                showMoreCta!.innerText = 'Hide Details';
            } else {
                showMoreCta!.innerText = 'Show More Details';
            }
        }
    };

    adjustFormPanelPositioning();

    /**
     * Calculate and set the initial padding for the content box
     * based on the height of the register prompt.
     */
    updateContentBoxPadding();
    manageStickyBar();

    /**
     * Manage the sticky bar on scroll and resize events.
     */
    window.addEventListener('scroll', manageStickyBar);
    window.addEventListener('resize', () => {
        updateContentBoxPadding();
        manageStickyBar();
    });

    // Add click event listener to showMoreCta
    if (showMoreCta) {
        showMoreCta.addEventListener('click', toggleMoreDetails);
    }

    window.addEventListener('resize', adjustFormPanelPositioning);

    // --- Register prompt fade-in/fade-out logic only for mobile view ---
    let footerObserver: IntersectionObserver | null = null;
    const isMobileScreenSize = window.outerWidth <= 767;
    if (registerPrompt && isMobileScreenSize) {
        // Mobile: fade in/out based only on footer visibility
        let isPromptVisible = false;
        let isAnimatingOut = false;
        let timeoutId: string | number | NodeJS.Timeout | null | undefined = null;
        const footer = document.querySelector('footer');
        if (!footer) {
            return;
        }
        let isFooterInView = false;
        const updatePromptState = (): void => {
            if (!isFooterInView) {
                if (!isPromptVisible && !isAnimatingOut) {
                    registerPrompt.classList.add('show', 'fade-in');
                    registerPrompt.classList.remove('fade-out');
                    isPromptVisible = true;
                    isAnimatingOut = false;
                    if (timeoutId) {
                        clearTimeout(timeoutId);
                        timeoutId = null;
                    }
                }
            } else if (isPromptVisible && !isAnimatingOut) {
                registerPrompt.classList.remove('fade-in');
                registerPrompt.classList.add('fade-out');
                isAnimatingOut = true;
                timeoutId = setTimeout(() => {
                    registerPrompt.classList.remove('show', 'fade-out');
                    isPromptVisible = false;
                    isAnimatingOut = false;
                    timeoutId = null;
                }, 300); // Match CSS transition duration
            }
        };
        footerObserver = new IntersectionObserver((entries => {
            entries.forEach(entry => {
                isFooterInView = entry.isIntersecting;
                updatePromptState();
            });
        }), { threshold: 0 });
        footerObserver.observe(footer);
        el.addEventListener('unload', () => {
            if (footerObserver) {
                footerObserver.disconnect();
                footerObserver = null;
            }
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        });
    } else if (registerPrompt) {
        // Desktop: always remove fade classes if present
        registerPrompt.classList.remove('show', 'fade-in', 'fade-out');
        if (footerObserver) {
            (footerObserver as IntersectionObserver).disconnect();
            footerObserver = null;
        }
    }
    window.addEventListener('resize', () => {
        if (registerPrompt && window.outerWidth > 767) {
            registerPrompt.classList.remove('show', 'fade-in', 'fade-out');
            if (footerObserver) {
                (footerObserver as IntersectionObserver).disconnect();
                footerObserver = null;
            }
        }
    });

    // --- Mobile register prompt observer logic ---
    let mobileObserver: IntersectionObserver | null = null;
    function enableMobilePromptObserver(): void {
        const registerPromptEl = document.querySelector('.register-prompt');
        const sidePanelEl = document.querySelector('.side-panel');
        const footerEl = document.querySelector('footer');
        if (!registerPromptEl || !sidePanelEl || !footerEl) {
            return;
        }
        registerPromptEl.classList.remove('show', 'fade-in', 'fade-out');
        let marker = document.querySelector('.side-panel-bottom-marker') as HTMLElement | null;
        if (!marker) {
            marker = document.createElement('div');
            marker.className = 'side-panel-bottom-marker';
            marker.style.cssText = 'position:absolute;bottom:0;left:0;width:100%;height:1px;';
            sidePanelEl.appendChild(marker);
        }
        if (mobileObserver) {
            mobileObserver.disconnect();
        }
        mobileObserver = new IntersectionObserver(entries => {
            if (window.outerWidth > 767) {
                // Always remove classes if not mobile
                registerPromptEl.classList.remove('show', 'fade-in', 'fade-out');
                return;
            }
            const footerRect = footerEl.getBoundingClientRect();
            const footerInView = footerRect.top < window.innerHeight && footerRect.bottom > 0;
            const entry = entries[0];
            if (footerInView) {
                registerPromptEl.classList.remove('show', 'fade-in', 'fade-out');
            } else if (!entry.isIntersecting) {
                registerPromptEl.classList.add('show', 'fade-in');
                registerPromptEl.classList.remove('fade-out');
            } else {
                registerPromptEl.classList.remove('fade-in');
                registerPromptEl.classList.add('fade-out');
            }
        }, { root: null, rootMargin: '0px', threshold: 0 });
        mobileObserver.observe(marker);
    }
    function disableMobilePromptObserver(): void {
        const registerPromptEl = document.querySelector('.register-prompt');
        if (registerPromptEl) {
            registerPromptEl.classList.remove('show', 'fade-in', 'fade-out');
        }
        if (mobileObserver) {
            mobileObserver.disconnect();
            mobileObserver = null;
        }
    }
    function handleResize(): void {
        const registerPromptEl = document.querySelector('.register-prompt');
        if (window.outerWidth <= 767) {
            enableMobilePromptObserver();
        } else {
            disableMobilePromptObserver();
            if (registerPromptEl) {
                registerPromptEl.classList.remove('show', 'fade-in', 'fade-out');
            }
        }
    }
    handleResize();
    window.addEventListener('resize', handleResize);
    el.addEventListener('unload', () => {
        disableMobilePromptObserver();
        window.removeEventListener('resize', handleResize);
    });

    el.addEventListener('unload', () => {
        window.removeEventListener('resize', adjustFormPanelPositioning);
        if (showMoreCta) {
            showMoreCta.removeEventListener('click', toggleMoreDetails);
        }
    });
};

export default FormPanel;
